import {defineStore} from "pinia";
import {ref, computed} from "vue";
import pulseBackend from "../backend/pulse.backend.ts";

export interface TimeAllocationCategoryDTO {
  key: string;
  label: string;
  description: string | null;
  color: string;
  colorDark?: string | null;
  order: number;
  active: boolean;
  usageCount: number;
}

export const useTimeAllocationCategoriesStore = defineStore('timeAllocationCategories', () => {
  const categories = ref([] as TimeAllocationCategoryDTO[]);
  const fetchedAt = ref(0);
  const loading = ref(false);
  const error = ref(null as string | null);

  const keys = computed(() => categories.value.map(c => c.key));
  const colorMap = computed(() => Object.fromEntries(categories.value.map(c => [c.key, c.color])) as Record<string, string>);
  const active = computed(() => categories.value.filter(c => c.active));
  const isLoaded = computed(() => categories.value.length > 0 && !loading.value);

  const fetch = async (force = false) => {
    if (loading.value) return;
    if (!force && categories.value.length && Date.now() - fetchedAt.value < 60_000) return; // cache 1m
    loading.value = true;
    try {
      const data = await pulseBackend.categories();
      categories.value = data as TimeAllocationCategoryDTO[];
      fetchedAt.value = Date.now();
      error.value = null;
    } catch (err: any) {
      console.error('Failed to load time allocation categories:', err);
      error.value = err?.message || 'Failed to load categories';
    } finally {
      loading.value = false;
    }
  };

  // Auto-load categories on store creation
  fetch();

  return {
    categories,
    fetchedAt,
    loading,
    error,
    keys,
    colorMap,
    active,
  isLoaded,
  fetch
  };
});
